import React, { useState } from 'react';
import { TwilioScript, Agent, Campaign, ConversationSummary, makeExotelCall } from '../utils/api';
import './OverviewDashboard.css';

interface OverviewDashboardProps {
  userRole: 'super_admin' | 'admin' | 'customer';
  scripts: TwilioScript[];
  agents: Agent[];
  campaigns: Campaign[];
  conversations: ConversationSummary[];
  setView: (view: 'dashboard' | 'scripts' | 'agents' | 'campaigns' | 'conversations' | 'users' | 'setup') => void;
}

const OverviewDashboard: React.FC<OverviewDashboardProps> = ({
  userRole,
  scripts,
  agents,
  campaigns,
  conversations,
  setView,
}) => {
  const [dialTo, setDialTo] = useState('');
  const [dialScriptId, setDialScriptId] = useState('');
  const [dialStatus, setDialStatus] = useState<'idle' | 'calling' | 'success' | 'error'>('idle');
  const [dialMessage, setDialMessage] = useState('');
  const [searchAction, setSearchAction] = useState('');

  const handleDial = async () => {
    if (!dialTo.trim()) {
      setDialMessage('Enter a phone number.');
      setDialStatus('error');
      return;
    }
    setDialStatus('calling');
    setDialMessage('');
    try {
      const res = await makeExotelCall(dialTo.trim(), dialScriptId || undefined);
      setDialStatus('success');
      setDialMessage(`✓ Call initiated — SID: ${res.callSid}`);
      setDialTo('');
    } catch (e: any) {
      setDialStatus('error');
      setDialMessage(e.message || 'Call failed');
    }
  };

  const quickActionsList = [
    { label: 'Create Script', view: 'scripts' as const },
    { label: 'Add Voice Agent', view: 'agents' as const },
    { label: 'Upload CSV Campaign', view: 'campaigns' as const },
    { label: 'View Call Logs', view: 'conversations' as const },
    { label: 'Add User Account', view: 'users' as const },
    { label: 'Read Setup Guide', view: 'setup' as const },
  ];

  const filteredQuickActions = quickActionsList.filter(act =>
    act.label.toLowerCase().includes(searchAction.toLowerCase())
  );

  return (
    <div className="dashboard-content">
      {/* ── Welcome Banner ── */}
      <div className="welcome-banner-card">
        <div className="welcome-banner-info">
          <h2>Welcome back, {userRole === 'super_admin' ? 'Super Admin' : 'Admin'}! 👋</h2>
          <p>Here's what's happening with your voice call platform today.</p>
        </div>
        <div className="welcome-banner-graphic">
          <div className="graphic-circle outer"></div>
          <div className="graphic-circle inner"></div>
        </div>
      </div>

      {/* ── Section Title ── */}
      <h3 className="section-overview-label">System Overview</h3>

      {/* ── System Overview Metric Cards ── */}
      <div className="metrics-summary-grid">
        {/* Total Call Records */}
        <div className="metric-box-card">
          <div className="metric-data-container">
            <div className="metric-header-row">
              <div className="metric-icon-badge purple">📞</div>
              <div className="mock-sparkline purple">
                <svg viewBox="0 0 100 30" className="sparkline">
                  <path d="M0 25 Q15 10, 30 20 T60 5 T90 2" fill="none" stroke="#9333ea" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </div>
            </div>
            <div className="metric-value-row">
              <h3>{conversations.length}</h3>
              <span className="metric-label">Total Calls</span>
              <span className="trend-stat positive">↑ 25% from last month</span>
            </div>
          </div>
        </div>

        {/* Active Voice Agents */}
        <div className="metric-box-card">
          <div className="metric-data-container">
            <div className="metric-header-row">
              <div className="metric-icon-badge green">🤖</div>
              <div className="mock-sparkline green">
                <svg viewBox="0 0 100 30" className="sparkline">
                  <path d="M0 20 Q15 25, 30 12 T60 22 T90 8" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </div>
            </div>
            <div className="metric-value-row">
              <h3>{agents.length}</h3>
              <span className="metric-label">Total Agents</span>
              <span className="trend-stat positive">↑ 14% from last month</span>
            </div>
          </div>
        </div>

        {/* Script Templates */}
        <div className="metric-box-card">
          <div className="metric-data-container">
            <div className="metric-header-row">
              <div className="metric-icon-badge orange">📝</div>
              <div className="mock-sparkline orange">
                <svg viewBox="0 0 100 30" className="sparkline">
                  <path d="M0 28 Q15 15, 30 25 T60 12 T90 5" fill="none" stroke="#ea580c" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </div>
            </div>
            <div className="metric-value-row">
              <h3>{scripts.length}</h3>
              <span className="metric-label">Total Scripts</span>
              <span className="trend-stat positive">↑ 6% from last month</span>
            </div>
          </div>
        </div>

        {/* Campaigns */}
        <div className="metric-box-card">
          <div className="metric-data-container">
            <div className="metric-header-row">
              <div className="metric-icon-badge blue">🚀</div>
              <div className="mock-sparkline blue">
                <svg viewBox="0 0 100 30" className="sparkline">
                  <path d="M0 15 H100" fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </div>
            </div>
            <div className="metric-value-row">
              <h3>{campaigns.length}</h3>
              <span className="metric-label">Your Campaigns</span>
              <span className="trend-stat">0% from last month</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Sub-columns: Account Details, Quick Actions, Outbound Dialer ── */}
      <div className="dashboard-columns-container">
        
        {/* Your Account */}
        <div className="panel-box-card card-third">
          <div className="card-header-with-icon">
            <span className="header-icon">👤</span>
            <h4>Your Account</h4>
          </div>
          <div className="account-details-list">
            <div className="detail-row-item">
              <span>Name</span>
              <strong>{userRole === 'super_admin' ? 'Super Admin' : 'Admin'}</strong>
            </div>
            <div className="detail-row-item">
              <span>Email</span>
              <strong>admin@flowai.com</strong>
            </div>
            <div className="detail-row-item">
              <span>Carrier API Status</span>
              <strong className="status-badge-active">Online (Exotel)</strong>
            </div>
            <div className="detail-row-item">
              <span>Database Connection</span>
              <strong className="status-badge-active">Connected (MySQL)</strong>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="panel-box-card card-third">
          <div className="card-header-with-icon">
            <span className="header-icon">⚡</span>
            <h4>Quick Actions</h4>
          </div>
          <div className="quick-actions-search-container">
            <input
              type="text"
              placeholder="Search actions..."
              value={searchAction}
              onChange={(e) => setSearchAction(e.target.value)}
              className="quick-actions-search-input"
            />
          </div>
          <div className="quick-actions-grid">
            {filteredQuickActions.map((act, index) => (
              <button
                key={index}
                className="action-pill-btn"
                onClick={() => setView(act.view)}
              >
                {act.label}
              </button>
            ))}
            {filteredQuickActions.length === 0 && (
              <div className="no-actions-found">No actions match search</div>
            )}
          </div>
        </div>

        {/* Quick Outbound Dial */}
        <div className="panel-box-card card-third">
          <div className="card-header-with-icon">
            <span className="header-icon">📞</span>
            <h4>Quick Outbound Dial</h4>
          </div>
          <p className="hint-label-small">
            Connect an agent to a specific customer number using a selected script.
          </p>
          <div className="quick-dialer-form">
            <div className="form-group-custom">
              <label>Customer Mobile Number (E.164)</label>
              <input
                type="text"
                placeholder="e.g. +918600432414"
                value={dialTo}
                onChange={(e) => {
                  setDialTo(e.target.value);
                  setDialStatus('idle');
                }}
                className="quick-dial-input"
              />
            </div>
            <div className="form-group-custom">
              <label>Choose Script Template</label>
              <select
                value={dialScriptId}
                onChange={(e) => setDialScriptId(e.target.value)}
                className="quick-dial-select"
              >
                <option value="">— Use Default Script —</option>
                {scripts.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            {dialMessage && (
              <div className={`dial-alert-message ${dialStatus === 'success' ? 'success' : 'error'}`}>
                {dialMessage}
              </div>
            )}
            <button
              className="dial-execute-btn"
              onClick={handleDial}
              disabled={dialStatus === 'calling'}
            >
              {dialStatus === 'calling' ? '⏳ Dials Connecting...' : '📞 Call Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewDashboard;
